import { ENV } from '@/constants';
import { exploreSchemaItemList } from '../exploreSchema';

export const schemaObject_AiCvPhotoGenerator = {
	'@context': 'https://schema.org',
	'@graph': [
		{
			'@type': 'WebPage',
			'@id': 'https://liftapp.ai/ai-cv-photo-generator',
			name: 'CV Photo AI — AI Resume Photo Generator | Lift',
			description:
				'Generate professional CV photos instantly with AI. No watermark. Upload photo, choose business headshot styles, and get studio-quality resume photos in minutes. Try now!',
			url: 'https://liftapp.ai/ai-cv-photo-generator',
			image: 'https://liftapp.ai/seo-image.webp',
			mainEntity: [
				{ '@id': 'https://liftapp.ai/ai-cv-photo-generator#product' },
				{ '@id': 'https://liftapp.ai/ai-cv-photo-generator#howto' },
				{ '@id': 'https://liftapp.ai/ai-cv-photo-generator#benefits' },
				{ '@id': 'https://liftapp.ai/ai-cv-photo-generator#faq' },
				{ '@id': 'https://liftapp.ai/ai-cv-photo-generator#why-lift' },
				{ '@id': 'https://liftapp.ai/ai-cv-photo-generator#explore-tools' },
				{ '@id': 'https://liftapp.ai/ai-cv-photo-generator#all-in-one' },
			],
		},
		// product
		{
			'@type': 'Product',
			'@id': 'https://liftapp.ai/ai-cv-photo-generator#product',
			name: 'AI CV Photo Generator Online',
			description:
				'Create professional CV photos in minutes. Get a polished, studio-quality resume photo without a physical photo shoot, no watermarks.',
			url: 'https://liftapp.ai/ai-cv-photo-generator',
			fileFormat: ['image/jpeg', 'image/png', 'image/webp'],
			memoryRequirements: 'Max file size: 20MB',
			brand: {
				'@type': 'Brand',
				name: 'Lift',
			},
			aggregateRating: {
				'@type': 'AggregateRating',
				ratingValue: '4.8',
				reviewCount: 130000,
				bestRating: '5',
				worstRating: '1',
			},
			interactionStatistic: [
				{
					'@type': 'InteractionCounter',
					interactionType: { '@type': 'InstallAction' },
					userInteractionCount: 13000000,
				},
			],
		},
		// howto
		{
			'@type': 'HowTo',
			'@id': 'https://liftapp.ai/ai-cv-photo-generator#howto',
			name: 'How it works',
			step: [
				{
					'@type': 'HowToStep',
					position: 1,
					name: 'Upload your photos',
					text: "Choose a photo from your device or take one using Lift's app",
				},
				{
					'@type': 'HowToStep',
					position: 2,
					name: 'Let the AI do the work',
					text: 'Watch as Lift generates professional CV photos automatically',
				},
				{
					'@type': 'HowToStep',
					position: 3,
					name: 'Customize adjustments',
					text: 'Fine-tune your image with easy-to-use editing tools',
				},
				{
					'@type': 'HowToStep',
					position: 4,
					name: 'Download and use',
					text: 'Download your image or publish it straight to your social feeds',
				},
			],
		},
		// benefit
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/ai-cv-photo-generator#benefits',
			name: 'Who can benefit from AI CV Photo Generator?',
			description:
				"Lift's AI CV Photo Generator is useful for professionals, creators, and businesses who want clean, distraction-free images for social media, e-commerce, marketing, or personal use.",
			itemListOrder: 'Unordered',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'Job Seekers & Career Switchers — Upgrade your LinkedIn profile and resume with confidence-boosting professional headshots. Stand out in competitive job markets with AI-generated photos that make powerful first impressions.',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'Freelancers & Solopreneurs — Build your personal brand with consistent, professional headshots for all platforms. Skip expensive photographer sessions and get multiple business-ready styles in one upload.',
				},
				{
					'@type': 'ListItem',
					position: 3,
					name: 'Remote Teams — Achieve uniform, professional team photos for company directories and About Us pages. Eliminate scheduling hassles and ensure every team member has high-quality corporate headshots.',
				},
				{
					'@type': 'ListItem',
					position: 4,
					name: 'Personal Brand Builders — Refresh your social media profiles, websites, and speaker bios with eye-catching professional portraits. Create authentic yet polished images that align perfectly with your brand identity.',
				},
				{
					'@type': 'ListItem',
					position: 5,
					name: 'Students & educators — Prepare professional-looking photos for internship applications, academic profiles, and networking platforms. Access affordable, studio-quality headshots without breaking your budget.',
				},
			],
			image: {
				'@type': 'ImageObject',
				contentUrl:
					'https://liftapp.ai/_next/static/media/benefits.3da6de53.webp',
				name: 'AI photo for resume use cases',
				description: 'AI photo for resume use cases',
			},
		},

		// faq
		{
			'@type': 'FAQPage',
			'@id': 'https://liftapp.ai/ai-cv-photo-generator#faq',
			mainEntity: [
				{
					'@type': 'Question',
					name: 'What is Lift AI CV Photo Generator?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'Lift AI CV Photo Generator is a premium online tool that instantly creates professional business headshots and resume photos with studio-quality results — ideal for LinkedIn profiles, CVs, corporate directories, and personal branding.',
					},
				},
				{
					'@type': 'Question',
					name: 'How does Lift AI CV Photo Generator work?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: "Simply upload a few selfies to Lift's AI headshot generator tool. Our AI automatically generates professional CV photos in multiple styles and backgrounds in minutes — no photography experience or studio needed. Download up to 20 professional headshots.",
					},
				},
				{
					'@type': 'Question',
					name: 'Will it keep hair, edges, and fine details?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'Yes. Lift AI CV Photo Generator uses advanced edge detection to preserve delicate elements like hair, fur, shadows, and soft textures — so you get clean, natural-looking cutouts without manual touch-ups.',
					},
				},
				{
					'@type': 'Question',
					name: 'Can I download the results without a watermark?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: "Absolutely. All images generated or edited in Lift can be exported without watermark when you're on the Pro plan. Download them in high-res PNG or JPG — ready for social media, print, ads, or e-commerce.",
					},
				},
			],
		},

		// why lift
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/ai-cv-photo-generator#why-lift',
			name: "Why Lift's AI CV Photo Generator stands out",
			description:
				"Lift's CV Photo Generator combines professional-grade AI, ease of use, and affordability — making it a trusted choice for millions of users worldwide.",
			itemListOrder: 'Unordered',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'High-quality results',
					description:
						'AI-powered technology delivers professional-grade visuals every time',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'Intelligent rendering',
					description:
						'AI tailors the effect to the scene and subject for optimal results',
				},
				{
					'@type': 'ListItem',
					position: 3,
					name: 'Cross-platform support',
					description: 'Available on iOS, Android, and Web for seamless access',
				},
				{
					'@type': 'ListItem',
					position: 4,
					name: 'Budget-friendly',
					description:
						'Save on costly designers with an affordable and intuitive tool',
				},
			],
			image: {
				'@type': 'ImageObject',
				contentUrl:
					'https://liftapp.ai/_next/static/media/whyUse.ae83a89c.webp',
				name: 'AI cv photo maker',
				description: 'AI cv photo maker',
			},
		},
		// explore
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/ai-cv-photo-generator#explore-tools',
			name: 'Explore the AI tools',
			description:
				'A curated list of AI-powered tools for image generation, enhancement, background removal, and more – all available on Lift.',
			itemListOrder: 'Unordered',
			...exploreSchemaItemList,
		},

		{
			'@type': 'SoftwareApplication',
			'@id': 'https://liftapp.ai/ai-cv-photo-generator#all-in-one',
			name: 'Try Lift – AI Content Creation Tools',
			description:
				'Your all-in-one tools to create, edit, and elevate your content. Create stunning content with AI tools, templates, and editing features effortlessly.',
			operatingSystem: 'iOS, Android, Web',
			applicationCategory: 'MultimediaApplication',
			url: `${ENV.LIFT_WEB_APP_URL}/sign-in?auto_login=true`,
			applicationSubCategory: 'Content Creation',
			publisher: {
				'@type': 'Organization',
				name: 'Lift',
				url: 'https://liftapp.ai',
			},
			potentialAction: [
				{
					'@type': 'ViewAction',
					name: 'Open Web App',
					target: `${ENV.LIFT_WEB_APP_URL}/sign-in?auto_login=true`,
				},
			],
		},

		{
			'@type': 'BreadcrumbList',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'Home',
					item: 'https://liftapp.ai',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'CV Photo Generator',
					item: 'https://liftapp.ai/ai-cv-photo-generator',
				},
			],
		},
	],
};
